import React, { useEffect, useState } from "react";
import { Drawer } from "vaul";
import {
  Download,
  Share,
  Plus,
  X,
  Smartphone,
  Monitor,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";
import { useInstalledState } from "../lib/useInstalledState";
import { promptInstall, useInstallable } from "../lib/pwaInstall";

function detectIOS() {
  const ua = navigator.userAgent || "";
  return /iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);
}
function detectAndroid() {
  return /Android/i.test(navigator.userAgent || "");
}

/**
 * Auto-detecting "Install LocalH2O" drawer.
 *
 * - iOS Safari: step-by-step Share → Add to Home Screen guide
 * - Android Chrome/Edge: native `beforeinstallprompt` button if available,
 *   otherwise menu instructions
 * - Desktop browsers: install via address-bar icon / browser menu, with a
 *   bookmark fallback
 * - Already installed: emerald "you're all set" state (driven by the
 *   shared `useInstalledState` hook so detection is consistent everywhere)
 *
 * Controlled component — the parent owns `open`/`onOpenChange` so tabs and
 * other surfaces can drive it.
 */
export default function InstallDrawer({ open, onOpenChange }) {
  const { t } = useI18n();
  const installed = useInstalledState();
  const canPromptNatively = useInstallable();
  const [platform, setPlatform] = useState("other"); // ios | android | desktop | other

  useEffect(() => {
    const ios = detectIOS();
    const android = detectAndroid();
    if (ios) setPlatform("ios");
    else if (android) setPlatform("android");
    else setPlatform("desktop");
  }, []);

  const triggerInstall = async () => {
    const outcome = await promptInstall();
    if (outcome === "accepted") {
      toast.success(t("install.installed_toast"));
      onOpenChange?.(false);
    } else if (outcome === "unavailable") {
      toast.info(t("install.no_prompt_available"));
    }
  };

  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange}>
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 bg-black/40 z-[120]" />
        <Drawer.Content
          className="fixed bottom-0 left-0 right-0 z-[125] bg-white rounded-t-3xl shadow-2xl flex flex-col max-h-[92dvh] outline-none"
          data-testid="install-drawer"
        >
          <Drawer.Title className="sr-only">{t("install.title")}</Drawer.Title>
          <Drawer.Description className="sr-only">
            {t("install.subtitle")}
          </Drawer.Description>

          <div className="flex-shrink-0 pt-2.5 pb-1.5 grid place-items-center">
            <div className="w-10 h-1 rounded-full bg-slate-300" />
          </div>

          <div className="flex items-center justify-between px-5 pb-3 border-b border-slate-100">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-2xl bg-sky-600 grid place-items-center text-white shrink-0">
                {installed ? (
                  <CheckCircle2 className="w-5 h-5" />
                ) : (
                  <Download className="w-5 h-5" />
                )}
              </div>
              <div className="min-w-0">
                <div className="font-display text-lg font-bold text-slate-900 truncate">
                  {installed ? t("install.installed_title") : t("install.title")}
                </div>
                <div className="text-xs text-slate-500 truncate">
                  {installed ? t("install.installed_subtitle") : t("install.subtitle")}
                </div>
              </div>
            </div>
            <Drawer.Close asChild>
              <button
                aria-label="Close"
                className="p-1.5 rounded-lg hover:bg-slate-100"
                data-testid="install-drawer-close"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </Drawer.Close>
          </div>

          <div className="px-5 py-5 space-y-4">
            {installed && (
              <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800 flex items-start gap-2.5">
                <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold">{t("install.installed_title")}</div>
                  <div className="mt-1 text-emerald-900/80">
                    {t("install.installed_body")}
                  </div>
                </div>
              </div>
            )}

            {!installed && platform === "ios" && (
              <div className="space-y-3" data-testid="install-platform-ios">
                <PlatformHeader
                  icon={<Smartphone className="w-4 h-4" />}
                  label={t("install.detected_ios")}
                />
                <Step num={1} text={t("pwa.ios_step1")}>
                  <Share className="w-4 h-4" />
                </Step>
                <Step num={2} text={t("pwa.ios_step2")}>
                  <Plus className="w-4 h-4" />
                </Step>
                <Step num={3} text={t("pwa.ios_step3")} />
              </div>
            )}

            {!installed && platform === "android" && (
              <div className="space-y-3" data-testid="install-platform-android">
                <PlatformHeader
                  icon={<Smartphone className="w-4 h-4" />}
                  label={t("install.detected_android")}
                />
                {canPromptNatively ? (
                  <button
                    onClick={triggerInstall}
                    className="w-full btn-primary py-3 inline-flex items-center justify-center gap-2"
                    data-testid="install-android-btn"
                  >
                    <Download className="w-4 h-4" />
                    {t("pwa.install_btn")}
                  </button>
                ) : (
                  <div className="rounded-xl bg-slate-50 border border-slate-200 p-3.5 text-sm text-slate-600 leading-snug">
                    {t("pwa.android_manual")}
                  </div>
                )}
              </div>
            )}

            {!installed && platform === "desktop" && (
              <div className="space-y-3" data-testid="install-platform-desktop">
                <PlatformHeader
                  icon={<Monitor className="w-4 h-4" />}
                  label={t("install.detected_desktop")}
                />
                {canPromptNatively ? (
                  <button
                    onClick={triggerInstall}
                    className="w-full btn-primary py-3 inline-flex items-center justify-center gap-2"
                    data-testid="install-desktop-btn"
                  >
                    <Download className="w-4 h-4" />
                    {t("pwa.install_btn")}
                  </button>
                ) : (
                  <div className="rounded-xl bg-slate-50 border border-slate-200 p-3.5 text-sm text-slate-600 leading-snug">
                    {t("install.desktop_manual")}
                  </div>
                )}
              </div>
            )}

            {!installed && platform === "other" && (
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-3.5 text-sm text-slate-600 leading-snug">
                {t("pwa.fallback")}
              </div>
            )}

            <p className="text-[11px] text-slate-400 leading-snug pt-1">
              {t("install.footer_note")}
            </p>
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

function PlatformHeader({ icon, label }) {
  return (
    <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-2.5 py-1">
      {icon}
      {label}
    </div>
  );
}

function Step({ num, text, children }) {
  return (
    <div className="flex items-start gap-3 text-sm text-slate-700">
      <div className="w-6 h-6 rounded-full bg-sky-100 text-sky-700 grid place-items-center text-[11px] font-bold shrink-0 mt-0.5">
        {num}
      </div>
      <div className="flex-1 leading-snug">
        {text}
        {children && (
          <span className="inline-flex items-center gap-1 ml-1 text-slate-500 align-middle">
            ({children})
          </span>
        )}
      </div>
    </div>
  );
}
